/**
 * 基础布局组件 - 温馨治愈风格
 * 应用柔和配色、圆角、轻柔阴影
 */

import React, { useEffect, useState } from 'react';
import { Layout, Menu, theme, Avatar, Dropdown, message } from 'antd';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  AppstoreOutlined,
  LogoutOutlined,
  SmileOutlined,
  SunOutlined,
  MoonOutlined,
  TeamOutlined,
  MenuOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { getCurrentUserMenuTree } from '../services/api/menu';
import type { Menu as MenuType } from '../types/menu';
import { logout } from '../services/api/auth';
import './BasicLayout.css';

const { Header, Sider, Content } = Layout;

const BasicLayout: React.FC = () => {
  const navigate = useNavigate();
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<{ username: string; nickname: string; avatar?: string } | null>(null);

  // 获取用户信息
  useEffect(() => {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsed = JSON.parse(storedUserInfo);
        setUserInfo({
          username: parsed.username || '',
          nickname: parsed.nickname || parsed.username || '用户',
          avatar: parsed.avatar,
        });
      } catch (e) {
        console.error('解析用户信息失败:', e);
      }
    }
  }, []);

  // 从后端获取菜单树
  const fetchMenuTree = async () => {
    setLoading(true);
    try {
      const menuTree = await getCurrentUserMenuTree();
      console.log('后端返回的菜单树:', menuTree);

      // 转换为 Ant Design Menu 组件需要的格式
      const transformMenu = (menus: MenuType[]): any[] => {
        return menus.map(menu => {
          // 菜单项直接使用path作为key（去掉/前缀），目录使用/menu-{id}
          const item: any = {
            key: (menu.path && menu.type === 1 && !menu.path.startsWith('/system')) ? menu.path.replace(/^\//, '') : `/menu-${menu.id}`,
            label: menu.name,
            icon: getMenuIcon(menu.icon || menu.name)
          };

          console.log('转换菜单项:', { id: menu.id, name: menu.name, path: menu.path, type: menu.type, children: menu.children?.length, finalKey: item.key });

          // 只有目录（type=0）才应该有子节点
          if (menu.children && menu.children.length > 0 && menu.type === 0) {
            item.children = transformMenu(menu.children);
          }

          return item;
        });
      };

      const transformed = transformMenu(menuTree);
      console.log('转换后的菜单:', transformed);
      setMenuItems(transformed);
    } catch (error) {
      console.error('获取菜单失败:', error);
      message.error('获取菜单失败');
    } finally {
      setLoading(false);
    }
  };

  // 根据菜单名称返回图标
  const getMenuIcon = (iconName: string) => {
    const normalized = iconName.toLowerCase().replace(/-/g, '');
    const iconMap: Record<string, JSX.Element> = {
      'dashboard': <DashboardOutlined />,
      'user': <UserOutlined />,
      'users': <UserOutlined />,
      'role': <TeamOutlined />,
      'roles': <TeamOutlined />,
      'menu': <MenuOutlined />,
      'menus': <MenuOutlined />,
      'application': <AppstoreOutlined />,
      'applications': <AppstoreOutlined />,
      'log': <FileTextOutlined />,
      'logs': <FileTextOutlined />,
      'setting': <SettingOutlined />,
      'settings': <SettingOutlined />,
      'tree': <TeamOutlined />,
      // 添加常见的 Ant Design 图标映射
      'useroutlined': <UserOutlined />,
      'teamoutlined': <TeamOutlined />,
      'menuoutlined': <MenuOutlined />,
      'appstoreoutlined': <AppstoreOutlined />,
      'filetextoutlined': <FileTextOutlined />,
      'settingoutlined': <SettingOutlined />,
      'dashboardoutlined': <DashboardOutlined />,
    };
    return iconMap[normalized] || <SettingOutlined />;
  };

  useEffect(() => {
    fetchMenuTree();
  }, []);

  const handleMenuClick = ({ key }: { key: string }) => {
    console.log('点击菜单:', { key });

    // 菜单项使用相对路径（如 user、role），直接跳转
    if (!key.startsWith('/menu-')) {
      navigate(key);
    } else {
      console.log('是目录项，不跳转');
    }
  };

  // 用户下拉菜单
  const userMenuItems = [
    { key: 'profile', label: '👤 个人中心' },
    { key: 'settings', label: '⚙️ 设置' },
    { type: 'divider' as const },
    { key: 'logout', label: '🚪 退出登录', icon: <LogoutOutlined /> },
  ];

  const handleUserMenuClick = async ({ key }: { key: string }) => {
    if (key === 'logout') {
      try {
        await logout();
      } catch (error) {
        console.error('退出登录失败:', error);
      } finally {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        navigate('/login');
      }
    }
  };

  // 获取当前时间段的问候语
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 6) return '夜深了';
    if (hour < 9) return '早上好';
    if (hour < 12) return '上午好';
    if (hour < 14) return '中午好';
    if (hour < 18) return '下午好';
    if (hour < 22) return '晚上好';
    return '夜深了';
  };

  const displayName = userInfo?.nickname || userInfo?.username || '用户';

  return (
    <Layout className="warm-layout">
      <Sider className="warm-sider" breakpoint="lg" collapsedWidth="0">
        {/* Logo - 温暖的木色背景 */}
        <div className="warm-logo">
          <SmileOutlined className="warm-logo-icon" />
          <span className="warm-logo-text">FunHua Admin</span>
        </div>

        {/* 侧边菜单 */}
        <Menu
          className="warm-menu"
          theme="light"
          mode="inline"
          items={menuItems}
          onClick={handleMenuClick}
          loading={loading ? { delay: 300 } : undefined}
        />
      </Sider>

      <Layout className="warm-layout-content">
        <Header className="warm-header">
          <div className="warm-header-left">
            <div className="warm-welcome">
              <SunOutlined className="warm-sun-icon" />
              <span>{getGreeting()}，{displayName}</span>
            </div>
          </div>

          <div className="warm-header-right">
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }}>
              <div className="warm-user-info">
                <Avatar
                  size="small"
                  style={{
                    backgroundColor: '#F8D7DA',
                    color: '#5C4033',
                  }}
                >
                  {displayName.charAt(0).toUpperCase()}
                </Avatar>
                <span className="warm-username">{displayName}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="warm-content">
          <div className="warm-content-inner">
            <Outlet />
          </div>

          {/* ICP 备案信息 */}
          <div className="warm-icp-footer">
            <p>
              <span>ICP 备案/许可证号：</span>
              <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer" className="icp-link">
                粤 ICP 备 2021167526 号
              </a>
              <span className="icp-divider">|</span>
              <span>主办单位名称：林秀彬</span>
              <span className="icp-divider">|</span>
              <span>主办单位性质：个人</span>
            </p>
            <p className="icp-sub">
              <span>ICP 备案/许可证号：</span>
              <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer" className="icp-link">
                粤 ICP 备 2021167526 号 -1
              </a>
              <span className="icp-date">审核通过日期：2021-12-09</span>
            </p>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default BasicLayout;
